/**
 * Core game type definitions for Playing History – Durak
 * Based on durak_history.html mockup and Proposal_concept_PlayingHistory_v1.md
 */

/** Card suits in standard order */
export type Suit = '♠' | '♥' | '♦' | '♣';

/** Card ranks in ascending order of value (6 lowest, A highest) */
export type Rank = '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';

/** Question difficulty tiers mapped to card ranks */
export type Difficulty = 'easy' | 'medium' | 'hard';

/** Card representation with computed value for comparisons */
export interface Card {
  suit: Suit;
  rank: Rank;
  value: number; // 0-8 for comparison
}

/** A play slot containing attacker and optional defender */
export interface PlaySlot {
  attacker: Card;
  defender: Card | null;
}

/** Game phase states */
export type GamePhase = 'attack' | 'defend' | 'draw' | 'ended';

/** Player role in current bout */
export type PlayerRole = 'attacker' | 'defender' | 'idle';

/** Minimal answered question reference for game state */
export interface AnsweredQuestionRef {
  id: string;
  wasCorrect: boolean;
  xpGain: number;
  answeredAt: number;
  era: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

/** Main game state interface */
export interface GameState {
  // Deck state
  deck: Card[];
  trump: Suit;
  trumpCard: Card;
  discard: Card[];

  // Player hands
  playerHand: Card[];
  botHand: Card[];

  // Play area
  playZone: PlaySlot[];

  // Game flow
  isPlayerAttacking: boolean;
  gamePhase: GamePhase;

  // Progression tracking
  xp: number;
  streak: number;
  bestStreak: number;
  correctCount: number;
  totalQuestions: number;
  answered: AnsweredQuestionRef[];

  // Pending actions (for question gating)
  pendingCard: Card | null;

  // Match timing
  matchStartTime: number | null;
}

/** Initial game state factory */
export function createInitialGameState(): GameState {
  return {
    deck: [],
    trump: '♠',
    trumpCard: { suit: '♠', rank: '6', value: 0 },
    discard: [],
    playerHand: [],
    botHand: [],
    playZone: [],
    isPlayerAttacking: true,
    gamePhase: 'attack',
    xp: 0,
    streak: 0,
    bestStreak: 0,
    correctCount: 0,
    totalQuestions: 0,
    answered: [],
    pendingCard: null,
    matchStartTime: null,
  };
}

/** Game configuration constants */
export const GAME_CONFIG = {
  /** Total cards in a Durak deck */
  DECK_SIZE: 36,

  /** Cards dealt to each player at start */
  INITIAL_HAND_SIZE: 6,

  /** Maximum cards that can be played in one bout */
  MAX_ATTACK_CARDS: 6,

  /** XP rewards by difficulty tier */
  XP_REWARDS: {
    easy: 10,
    medium: 25,
    hard: 50,
  } as const,

  /** Time limits by difficulty tier (seconds) */
  TIME_LIMITS: {
    easy: 30,
    medium: 45,
    hard: 60,
  } as const,

  /** Cost for hints (XP) */
  HINT_COST: 10,

  /** Cost for timer extension (XP) */
  EXTEND_COST: 5,

  /** Bonus XP for correct perspective challenge */
  PERSPECTIVE_BONUS: 15,

  /** Seconds added by timer extension */
  EXTEND_SECONDS: 15,
} as const;

/**
 * Card rank to difficulty mapping.
 *
 * Aligned with Durak card strength: in Russian Durak the rank order from
 * lowest to highest is 6, 7, 8, 9, 10, J, Q, K, A. We map the four face
 * cards + Ace (the strongest in the game) to the hard tier, the three
 * mid-rank cards (8, 9, 10) to medium, and the two lowest non-trump
 * "fillers" (6, 7) to easy. This gives a graduated 8 / 12 / 16 card split
 * that mirrors a player's risk: high-value cards are the ones you most
 * want to actually play, so they should also command the hardest
 * historical questions.
 */
export const RANK_DIFFICULTY: Record<Rank, Difficulty> = {
  '6': 'easy',
  '7': 'easy',
  '8': 'medium',
  '9': 'medium',
  '10': 'medium',
  'J': 'hard',
  'Q': 'hard',
  'K': 'hard',
  'A': 'hard',
} as const;

/** Rank to numeric value mapping for comparisons */
export const RANK_VALUES: Record<Rank, number> = {
  '6': 0,
  '7': 1,
  '8': 2,
  '9': 3,
  '10': 4,
  'J': 5,
  'Q': 6,
  'K': 7,
  'A': 8,
} as const;

/** All suits in standard order */
export const SUITS: Suit[] = ['♠', '♥', '♦', '♣'];

/** All ranks in ascending order */
export const RANKS: Rank[] = ['6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

/** Check if a suit is red (hearts or diamonds) */
export function isRedSuit(suit: Suit): boolean {
  return suit === '♥' || suit === '♦';
}

/** Check if a card is a trump card */
export function isTrump(card: Card, trumpSuit: Suit): boolean {
  return card.suit === trumpSuit;
}

/** Get difficulty for a card rank */
export function getCardDifficulty(rank: Rank): Difficulty {
  return RANK_DIFFICULTY[rank];
}

/** Get XP reward for a difficulty tier */
export function getXpReward(difficulty: Difficulty): number {
  return GAME_CONFIG.XP_REWARDS[difficulty];
}

/** Get time limit for a difficulty tier */
export function getTimeLimit(difficulty: Difficulty): number {
  return GAME_CONFIG.TIME_LIMITS[difficulty];
}
